import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-32 pb-24" data-testid="contact-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-6">
            Message Sent
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Thank you for reaching out! I'll get back to you as soon as possible.
          </p>
          <Button 
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: '', email: '', subject: '', message: '' });
            }}
            variant="outline"
            className="rounded-full"
          >
            SEND ANOTHER MESSAGE
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="contact-page">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-none mb-6" data-testid="contact-title">
            Get in Touch
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            Have a question about an artwork, interested in a commission, or just want to say hello? 
            I'd love to hear from you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h2 className="font-serif text-2xl mb-4">Contact Information</h2>
              <div className="flex items-center gap-3 text-muted-foreground">
                <Mail className="h-5 w-5" />
                <a href="mailto:hello@themoderngallery.art" className="hover:text-accent transition-colors">
                  hello@themoderngallery.art
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="font-serif text-xl mb-3">Response Time</h3>
              <p className="text-muted-foreground">
                I typically respond within 24-48 hours. For urgent inquiries about purchases, 
                please include your order number in the subject line.
              </p>
            </div>

            <div>
              <h3 className="font-serif text-xl mb-3">Commissions</h3>
              <p className="text-muted-foreground">
                Interested in a custom piece? I'm always open to discussing new projects. 
                Please share your vision and I'll let you know if it's something we can create together.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-card border border-border rounded-lg p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">NAME</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">EMAIL</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <Label htmlFor="subject">SUBJECT</Label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="What is this regarding?"
                  data-testid="contact-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="message">MESSAGE</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Your message..."
                  data-testid="contact-message-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full rounded-full"
                disabled={submitting}
                data-testid="contact-submit-btn"
              >
                {submitting ? (
                  'SENDING...'
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    SEND MESSAGE
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
